import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { TemplateSelector, replaceMergeFields } from '@/components/email/EmailTemplateSelector';

export default function EmailComposeDialog({ open, onClose, lead }) {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      Promise.all([
        api.getEmailAccounts().catch(() => ({ data: [] })),
        api.getEmailTemplates().catch(() => ({ data: [] })),
      ]).then(([accRes, tmplRes]) => {
        setAccounts(accRes.data);
        setTemplates(tmplRes.data);
        if (accRes.data.length > 0) setSelectedAccount(accRes.data[0].id);
      }).finally(() => setLoading(false));
      setTo(lead?.email || '');
      setSubject('');
      setBody('');
    }
  }, [open, lead]);

  const handleApplyTemplate = (templateId) => {
    const tmpl = templates.find(t => t.id === templateId);
    if (!tmpl) return;
    setSubject(replaceMergeFields(tmpl.subject, lead, user));
    setBody(replaceMergeFields(tmpl.body, lead, user));
    toast.success(`Template "${tmpl.name}" applied`);
  };

  const handleSend = async () => {
    if (!selectedAccount || !to || !subject || !body) {
      toast.error('Please fill in all fields');
      return;
    }
    setSending(true);
    try {
      await api.sendEmailViaProvider({
        account_id: selectedAccount,
        to,
        subject,
        body: `<div style="font-family:'IBM Plex Sans',Arial,sans-serif;font-size:14px;line-height:1.6;color:#0F172A">${body.replace(/\n/g, '<br>')}</div>`,
        lead_id: lead?.id || '',
      });
      toast.success(`Email sent to ${to}`);
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send email');
    }
    setSending(false);
  };

  const selectedAcc = accounts.find(a => a.id === selectedAccount);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="email-compose-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Mail className="w-5 h-5" /> Send Email
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
        ) : accounts.length === 0 ? (
          <div className="py-6 text-center">
            <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 mb-2">No email accounts connected</p>
            <p className="text-xs text-slate-400">Go to Settings to connect your Gmail or Outlook account.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <TemplateSelector templates={templates} onApply={handleApplyTemplate} />

            <div>
              <Label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">From</Label>
              <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="email-from-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.email} ({a.provider === 'google' ? 'Gmail' : 'Outlook'})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">To</Label>
              <Input value={to} onChange={e => setTo(e.target.value)} placeholder="recipient@example.com" className="mt-1 rounded-sm" data-testid="email-to-input" />
            </div>

            <div>
              <Label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Subject</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject..." className="mt-1 rounded-sm" data-testid="email-subject-input" />
            </div>

            <div>
              <Label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Message</Label>
              <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Write your email..." rows={6} className="mt-1 w-full border border-slate-200 dark:border-slate-700 rounded-sm px-3 py-2 text-sm text-slate-900 dark:text-slate-100 dark:bg-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-[#0047AB] resize-none" data-testid="email-body-input" />
            </div>

            {selectedAcc && (
              <p className="text-[10px] text-slate-400">Sending via {selectedAcc.provider === 'google' ? 'Gmail' : 'Outlook'} ({selectedAcc.email})</p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} className="rounded-sm">Cancel</Button>
          {accounts.length > 0 && (
            <Button onClick={handleSend} disabled={sending || !to || !subject || !body} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" data-testid="email-send-btn">
              {sending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-1" /> Send Email</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
